import apiClient from './apiClient';
import { readAuthHomeBootResponse, buildHomeBootPath, homeCalendarRequestScope, assertHomeCalendarResponseScope, isHomeCalendarResponseCurrent } from '@liquid-spirit/api/domains/auth';
import { readApiErrorCode, readAuthErrorCode } from '@liquid-spirit/api/client';
import { resolveStoredAuthToken } from '../utils/storage';
import { credentialCacheScope } from './credentialCacheScope';

// Long enough for every Home provider to consume the login payload, but short
// enough that later route visits use their normal query caches/refetch rules.
const HOME_BOOT_TTL_MS = 15_000;
const HOME_BOOT_FAILURE_TTL_MS = 5_000;

const payloadCache = new Map();
const failureCache = new Map();
const inFlightRequests = new Map();

const buildScopeKey = ({ token, userId = null, communityId = null, displayTimeZone } = {}) =>
  JSON.stringify([credentialCacheScope(token ?? resolveStoredAuthToken()), userId, communityId,
    homeCalendarRequestScope({ displayTimeZone }).key]);
const clonePayload = payload => structuredClone(readAuthHomeBootResponse(payload));

const readFreshEntry = (entries, key, ttlMs) => {
  const entry = entries.get(key);
  if (!entry) return null;
  if (Date.now() < entry.storedAt || Date.now() - entry.storedAt >= ttlMs) {
    entries.delete(key);
    return null;
  }
  return entry.value;
};

export const clearHomeBootCache = () => {
  payloadCache.clear();
  failureCache.clear();
  inFlightRequests.clear();
};

export const fetchHomeBoot = async ({
  token,
  userId = null,
  communityId = null,
  force = false,
  displayTimeZone,
} = {}) => {
  token = token ?? resolveStoredAuthToken();
  const calendar = homeCalendarRequestScope({ displayTimeZone });
  const scopeKey = buildScopeKey({ token, userId, communityId, displayTimeZone });

  if (!force) {
    const cachedPayload = readFreshEntry(payloadCache, scopeKey, HOME_BOOT_TTL_MS);
    if (cachedPayload && isHomeCalendarResponseCurrent(cachedPayload.home, calendar)) return clonePayload(cachedPayload);
    if (cachedPayload) payloadCache.delete(scopeKey);

    const cachedFailure = readFreshEntry(failureCache, scopeKey, HOME_BOOT_FAILURE_TTL_MS);
    if (cachedFailure) throw cachedFailure;
  } else {
    payloadCache.delete(scopeKey);
    failureCache.delete(scopeKey);
  }

  const existingRequest = inFlightRequests.get(scopeKey);
  if (existingRequest) return clonePayload(await existingRequest);

  const request = apiClient.request(buildHomeBootPath(calendar.query), {
    method: 'GET',
    token,
    contract: 'raw',
    throwOnError: false,
  }).then(({ response, payload }) => {
    if (!response.ok) {
      const error = new Error(payload?.message || 'Failed to load home boot payload');
      error.status = response.status;
      error.data = payload;
      error.apiCode = readApiErrorCode(response, payload);
      error.authCode = readAuthErrorCode(response, payload);
      throw error;
    }

    readAuthHomeBootResponse(payload);
    assertHomeCalendarResponseScope(payload.home, calendar);

    if (inFlightRequests.get(scopeKey) === request) {
      payloadCache.set(scopeKey, { value: clonePayload(payload), storedAt: Date.now() });
    }
    return payload;
  }).catch((error) => {
    if (inFlightRequests.get(scopeKey) === request && error?.code !== 'INVALID_API_RESPONSE') {
      failureCache.set(scopeKey, { value: error, storedAt: Date.now() });
    }
    throw error;
  }).finally(() => {
    if (inFlightRequests.get(scopeKey) === request) {
      inFlightRequests.delete(scopeKey);
    }
  });

  inFlightRequests.set(scopeKey, request);
  return clonePayload(await request);
};

export const fetchHomeBootSlice = async (sliceName, options = {}, fallbackLoader = null) => {
  // Optional slices contain raw resources. They can share the sign-in boot
  // across viewer preferences, but never across credentials/accounts/communities.
  // Home itself must always use its exact calendar scope.
  const owner = JSON.parse(buildScopeKey(options));
  const shareable = sliceName !== 'home' && !options.force
    ? [...new Set([...inFlightRequests.keys(), ...payloadCache.keys()])].find(key => {
      const candidate = JSON.parse(key);
      return candidate[0] === owner[0] && candidate[1] === owner[1]
        && (!options.communityId || candidate[2] === owner[2])
        && (inFlightRequests.has(key) || readFreshEntry(payloadCache, key, HOME_BOOT_TTL_MS));
    }) : null;
  if (typeof fallbackLoader === 'function' && !options.force && !shareable && sliceName !== 'home') {
    return fallbackLoader();
  }
  try {
    const payload = shareable
      ? clonePayload(await (inFlightRequests.get(shareable) || readFreshEntry(payloadCache, shareable, HOME_BOOT_TTL_MS)))
      : await fetchHomeBoot(options);
    const unavailable = Array.isArray(payload.unavailable) ? payload.unavailable : [];
    if (unavailable.includes(sliceName) || typeof payload[sliceName] === 'undefined') {
      throw new Error(`Home boot slice unavailable: ${sliceName}`);
    }
    return payload[sliceName];
  } catch (error) {
    if (typeof fallbackLoader !== 'function') throw error;
    return fallbackLoader(error);
  }
};

export default {
  clearHomeBootCache,
  fetchHomeBoot,
  fetchHomeBootSlice,
};
